// =====================================================================
// Palettes.jsx - Pagina principala M-005 cu 3 tabs interne (19-05-2026)
// =====================================================================
// Layout Option C: 1 link in sidebar (/palettes), 3 vue-uri interne.
//
// Tabs:
//   1. Mouvements (default) - lista PalletMovement + filtre + buton "+ Nouveau"
//   2. Facturation          - pivot recipient x palletType x date + checkbox bulk
//   3. Types (catalog)      - lista PalletType + buton "+ Nouveau type"
//
// recipientType = 'TRANSPORTER' | 'CLIENT'
// Pivot grupeaza dupa recipient = carrier (daca TRANSPORTER) sau project (daca CLIENT).
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  palletTypes as typesApi,
  palletMovements as movsApi,
  projects as projectsApi,
  tripCarriers as carriersApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import MouvementFormModal from './MouvementFormModal.jsx';
import TypeFormModal from './TypeFormModal.jsx';

const PAGE_SIZE = 80;

const RECIPIENT_LABELS_FR = {
  TRANSPORTER: 'Transporteur',
  CLIENT:      'Client',
};

const RECIPIENT_BADGE_STYLE = {
  TRANSPORTER: { background: '#e0e7ff', color: '#3730a3' },
  CLIENT:      { background: '#fef3c7', color: '#92400e' },
};

const INVOICED_BADGE_STYLE = {
  yes: { background: '#d1fae5', color: '#065f46' },
  no:  { background: '#fef3c7', color: '#92400e' },
};

const inputBase = {
  padding: '0.4rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.8125rem',
  background: '#fff',
};

const btnPrimary = {
  background: '#2563eb', color: '#fff', border: 0,
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnSuccess = {
  background: '#059669', color: '#fff', border: 0,
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnPager = (disabled) => ({
  padding: '0.25rem 0.5rem',
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  fontSize: '0.875rem',
});

const exportBtnStyle = (format, loading) => ({
  padding: '0.375rem 0.75rem',
  background: loading ? '#f1f5f9' : (format === 'xlsx' ? '#16a34a' : '#3b82f6'),
  color: loading ? '#94a3b8' : 'white',
  border: '1px solid ' + (loading ? '#cbd5e1' : (format === 'xlsx' ? '#15803d' : '#2563eb')),
  borderRadius: 4,
  fontSize: '0.8125rem',
  fontWeight: 500,
  cursor: loading ? 'wait' : 'pointer',
});

const thStyle = {
  padding: '0.625rem 0.75rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  borderBottom: '1px solid #e5e7eb',
};

const tdStyle = {
  padding: '0.625rem 0.75rem',
  verticalAlign: 'middle',
  color: '#374151',
  fontSize: '0.875rem',
};

const tabBtn = (active) => ({
  background: 'transparent',
  border: 0,
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1,
});

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return ''; }
}

// =====================================================================
// MAIN
// =====================================================================
export default function Palettes() {
  const { can } = useAuth();

  const canReadTracking = can('pallet_tracking.read.all');
  const canCreateMov    = can('pallet_tracking.create.all') || can('pallet_tracking.create.own');
  const canInvoiceMov   = can('pallet_tracking.update.all');
  const canReadTypes    = can('inventory.read.all');
  const canCreateType   = can('inventory.create.all');

  const [activeTab, setActiveTab] = useState('mouvements');

  if (!canReadTracking && !canReadTypes) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Palettes in/out</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Palettes in/out</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-005 — suivi mouvements</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Suivi des mouvements de palettes (entrées + sorties par transporteur ou client).
      </p>

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canReadTracking && (
          <button style={tabBtn(activeTab === 'mouvements')} onClick={() => setActiveTab('mouvements')}>
            🚚 Mouvements
          </button>
        )}
        {canReadTracking && (
          <button style={tabBtn(activeTab === 'facturation')} onClick={() => setActiveTab('facturation')}>
            💰 Facturation
          </button>
        )}
        {canReadTypes && (
          <button style={tabBtn(activeTab === 'types')} onClick={() => setActiveTab('types')}>
            🎫 Types (catalogue)
          </button>
        )}
      </div>

      {activeTab === 'mouvements'  && canReadTracking && <MouvementsTab canCreate={canCreateMov} />}
      {activeTab === 'facturation' && canReadTracking && <FacturationTab canInvoice={canInvoiceMov} />}
      {activeTab === 'types'       && canReadTypes    && <TypesTab canCreate={canCreateType} />}
    </div>
  );
}

// =====================================================================
// TAB 1: MOUVEMENTS
// =====================================================================
function MouvementsTab({ canCreate }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx' | null

  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');
  const [filterRecipient, setFilterRecip]   = useState(''); // '' | 'TRANSPORTER' | 'CLIENT'
  const [filterInvoiced, setFilterInvoiced] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  function openCreate() { setEditingId(null); setShowModal(true); }
  function openEdit(id) { setEditingId(id); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditingId(null); }
  function onSaved()    { closeModal(); fetchList(); }

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (filterDateFrom)  params.dateFrom = filterDateFrom;
      if (filterDateTo)    params.dateTo   = filterDateTo;
      if (filterRecipient) params.recipientType = filterRecipient;
      if (filterInvoiced)  params.invoiced = filterInvoiced;

      const res = await movsApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('[MouvementsTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterDateFrom, filterDateTo, filterRecipient, filterInvoiced]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Export handler (CSV sau XLSX) - foloseste filtre curente
  async function handleExport(format) {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format });
      if (filterDateFrom)  params.append('dateFrom', filterDateFrom);
      if (filterDateTo)    params.append('dateTo', filterDateTo);
      if (filterRecipient) params.append('recipientType', filterRecipient);
      if (filterInvoiced)  params.append('invoiced', filterInvoiced);

      const response = await fetch(`/api/pallet-movements/export?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errData.message || "Erreur lors de l'export");
      }
      const cd = response.headers.get('Content-Disposition') || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : `palettes.${format}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Palettes] export error', err);
      alert(err.message || "Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }} style={inputBase} title="Date début" />
        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>→</span>
        <input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1); }} style={inputBase} title="Date fin" />

        <select value={filterRecipient} onChange={e => { setFilterRecip(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 150 }}>
          <option value="">Tous destinataires</option>
          <option value="TRANSPORTER">Transporteur</option>
          <option value="CLIENT">Client</option>
        </select>

        <select value={filterInvoiced} onChange={e => { setFilterInvoiced(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 130 }}>
          <option value="">Tous statuts</option>
          <option value="false">Non facturés</option>
          <option value="true">Facturés</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{total} mouvement{total !== 1 ? 's' : ''}</span>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('csv', exporting === 'csv')}
            title="Exporter en CSV (UTF-8 BOM, compatible Excel)"
          >
            {exporting === 'csv' ? '⏳ Export…' : `📥 Exporter CSV (${total})`}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('xlsx', exporting === 'xlsx')}
            title="Exporter en XLSX avec mise en forme"
          >
            {exporting === 'xlsx' ? '⏳ Export…' : '📊 Exporter XLSX'}
          </button>
          {canCreate && (
            <button style={btnPrimary} onClick={openCreate}>+ Nouveau mouvement</button>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Réf</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Destinataire</th>
                <th style={thStyle}>Type palette</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Entrées</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Sorties</th>
                <th style={thStyle}>Statut</th>
                <th style={thStyle}>Resp.</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun mouvement à afficher.</td></tr>
              )}
              {items.map(m => {
                const recipName = m.recipientType === 'TRANSPORTER' ? (m.carrier?.name || '—') : (m.project?.name || '—');
                return (
                  <tr key={m.id} onClick={() => openEdit(m.id)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>{m.ref}</td>
                    <td style={tdStyle}>{formatDateFR(m.date)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ ...RECIPIENT_BADGE_STYLE[m.recipientType], padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                          {RECIPIENT_LABELS_FR[m.recipientType] || m.recipientType}
                        </span>
                        <span style={{ fontWeight: 500 }}>{recipName}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 500 }}>{m.palletType?.code}</span>
                      <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: 6 }}>{m.palletType?.label}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color: m.palettesIn > 0 ? '#065f46' : '#9ca3af' }}>
                      {m.palettesIn > 0 ? `+${m.palettesIn}` : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color: m.palettesOut > 0 ? '#991b1b' : '#9ca3af' }}>
                      {m.palettesOut > 0 ? `−${m.palettesOut}` : '—'}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...(m.invoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                        {m.invoiced ? '✓ Facturé' : 'Non facturé'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.8125rem', color: '#6b7280' }}>{m.responsible?.name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button style={btnPager(page === 1)} disabled={page === 1} onClick={() => setPage(page - 1)}>‹ Précédent</button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Page {page} sur {totalPages}</span>
          <button style={btnPager(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(page + 1)}>Suivant ›</button>
        </div>
      )}

      {showModal && (
        <MouvementFormModal movementId={editingId} onClose={closeModal} onSaved={onSaved} />
      )}
    </>
  );
}

// =====================================================================
// TAB 2: FACTURATION
// =====================================================================
function FacturationTab({ canInvoice }) {
  const [movs, setMovs]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [bulkBusy, setBulkBusy]         = useState(false);

  const [year, setYear]   = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const fetchMovs = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo  = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const res = await movsApi.list({ dateFrom, dateTo, pageSize: 500 });
      setMovs(res.items || []);
    } catch (err) {
      console.error('[FacturationTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setMovs([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchMovs(); }, [fetchMovs]);

  // Build pivot: byRecipient = Map<recipientKey, {name, recipientType, types: Map<palletTypeCode, byDate Map>, movIds, allInvoiced}>
  const pivot = (() => {
    const byRecipient = new Map();
    for (const m of movs) {
      const rkey = m.recipientType === 'TRANSPORTER' ? `T:${m.carrierId}` : `C:${m.projectId}`;
      const rname = m.recipientType === 'TRANSPORTER' ? (m.carrier?.name || '—') : (m.project?.name || '—');
      const ptCode = m.palletType?.code || '?';
      const dateKey = (m.date || '').slice(0, 10);

      if (!byRecipient.has(rkey)) {
        byRecipient.set(rkey, {
          key: rkey, name: rname, recipientType: m.recipientType,
          types: new Map(), movIds: [], allInvoiced: true,
        });
      }
      const recip = byRecipient.get(rkey);
      recip.movIds.push(m.id);
      if (!m.invoiced) recip.allInvoiced = false;

      if (!recip.types.has(ptCode)) recip.types.set(ptCode, { code: ptCode, label: m.palletType?.label, byDate: new Map() });
      const t = recip.types.get(ptCode);
      if (!t.byDate.has(dateKey)) t.byDate.set(dateKey, { in: 0, out: 0, movIds: [], allInvoiced: true });
      const cell = t.byDate.get(dateKey);
      cell.in  += m.palettesIn || 0;
      cell.out += m.palettesOut || 0;
      cell.movIds.push(m.id);
      if (!m.invoiced) cell.allInvoiced = false;
    }
    return Array.from(byRecipient.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const allDates = (() => {
    const s = new Set();
    for (const m of movs) {
      const d = (m.date || '').slice(0, 10);
      if (d) s.add(d);
    }
    return Array.from(s).sort();
  })();

  function toggleRecipient(recip) {
    const next = new Set(selectedIds);
    const allSelected = recip.movIds.every(id => next.has(id));
    if (allSelected) recip.movIds.forEach(id => next.delete(id));
    else recip.movIds.forEach(id => next.add(id));
    setSelectedIds(next);
  }

  function toggleSelectAll() {
    if (selectedIds.size === movs.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(movs.map(m => m.id)));
  }

  async function handleBulkInvoice() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Marquer ${selectedIds.size} mouvement(s) comme facturé(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await movsApi.bulkInvoice({ ids: Array.from(selectedIds) });
      alert(`Facturés: ${res.invoiced}\nDéjà facturés (skip): ${res.alreadyInvoiced}\nIntrouvables: ${res.notFound}`);
      await fetchMovs();
    } catch (err) {
      console.error('[bulk invoice]', err);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  async function handleBulkUninvoice() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Annuler la facturation pour ${selectedIds.size} mouvement(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await movsApi.bulkUninvoice({ ids: Array.from(selectedIds) });
      alert(`Réversés: ${res.uninvoiced}\nDéjà non facturés (skip): ${res.alreadyUninvoiced}\nIntrouvables: ${res.notFound}`);
      await fetchMovs();
    } catch (err) {
      console.error('[bulk uninvoice]', err);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  const years = [year - 1, year, year + 1];
  const months = [
    { v: 1, l: 'Janvier' }, { v: 2, l: 'Février' }, { v: 3, l: 'Mars' }, { v: 4, l: 'Avril' },
    { v: 5, l: 'Mai' }, { v: 6, l: 'Juin' }, { v: 7, l: 'Juillet' }, { v: 8, l: 'Août' },
    { v: 9, l: 'Septembre' }, { v: 10, l: 'Octobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Décembre' },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>Période:</span>
        <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} style={inputBase}>
          {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} style={inputBase}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
          {canInvoice && (
            <>
              <button style={{ ...btnSuccess, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkInvoice}>
                ✓ Marquer facturé
              </button>
              <button style={{ ...btnPager(false), opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkUninvoice}>
                Annuler facturation
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && movs.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 6 }}>
          Aucun mouvement pour cette période.
        </div>
      )}

      {!loading && movs.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ ...thStyle, width: 36 }}>
                  <input type="checkbox" checked={selectedIds.size === movs.length && movs.length > 0} onChange={toggleSelectAll} />
                </th>
                <th style={thStyle}>Destinataire / Type</th>
                {allDates.map(d => (
                  <th key={d} style={{ ...thStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDateFR(d)}</th>
                ))}
                <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {pivot.map(recip => (
                <RecipientRows key={recip.key} recip={recip} allDates={allDates} selectedIds={selectedIds} onToggle={() => toggleRecipient(recip)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RecipientRows({ recip, allDates, selectedIds, onToggle }) {
  const totalSelected = recip.movIds.filter(id => selectedIds.has(id)).length;
  const allSelected = totalSelected === recip.movIds.length && recip.movIds.length > 0;
  const someSelected = totalSelected > 0 && totalSelected < recip.movIds.length;

  return (
    <>
      <tr style={{ borderTop: '2px solid #e5e7eb', background: '#fafafa' }}>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem' }}>
          <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={onToggle} disabled={recip.allInvoiced} />
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#111827' }}>
          <span style={{ ...RECIPIENT_BADGE_STYLE[recip.recipientType], padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.625rem', fontWeight: 500, marginRight: 8 }}>
            {RECIPIENT_LABELS_FR[recip.recipientType]}
          </span>
          {recip.name}
        </td>
        {allDates.map(d => <td key={d} style={tdStyle}></td>)}
        <td style={{ ...tdStyle, textAlign: 'center' }}>
          <span style={{ ...(recip.allInvoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
            {recip.allInvoiced ? '✓ Tous facturés' : 'Non facturé'}
          </span>
        </td>
      </tr>
      {Array.from(recip.types.values()).map(t => (
        <tr key={t.code} style={{ borderTop: '1px solid #f3f4f6' }}>
          <td style={tdStyle}></td>
          <td style={{ ...tdStyle, padding: '0.4rem 0.75rem 0.4rem 2rem', color: '#374151', fontSize: '0.8125rem' }}>
            <strong>{t.code}</strong> <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>({t.label})</span>
          </td>
          {allDates.map(d => {
            const cell = t.byDate.get(d);
            return (
              <td key={d} style={{ ...tdStyle, padding: '0.4rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {cell ? (
                  <span style={{ color: cell.allInvoiced ? '#9ca3af' : '#111827' }}>
                    {cell.in > 0 && <span style={{ color: '#065f46' }}>+{cell.in}</span>}
                    {cell.in > 0 && cell.out > 0 && ' / '}
                    {cell.out > 0 && <span style={{ color: '#991b1b' }}>−{cell.out}</span>}
                  </span>
                ) : <span style={{ color: '#d1d5db' }}>—</span>}
              </td>
            );
          })}
          <td style={tdStyle}></td>
        </tr>
      ))}
    </>
  );
}

// =====================================================================
// TAB 3: TYPES
// =====================================================================
function TypesTab({ canCreate }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filterActive, setFilterActive] = useState('true');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  function openCreate() { setEditingId(null); setShowModal(true); }
  function openEdit(id) { setEditingId(id); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditingId(null); }
  function onSaved()    { closeModal(); fetchList(); }

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { pageSize: 100 };
      if (filterActive) params.isActive = filterActive;
      const res = await typesApi.list(params);
      setItems(res.items || []);
    } catch (err) {
      console.error('[TypesTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterActive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)} style={{ ...inputBase, minWidth: 120 }}>
          <option value="true">Actifs</option>
          <option value="false">Inactifs</option>
          <option value="">Tous</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{items.length} type{items.length !== 1 ? 's' : ''}</span>
          {canCreate && (
            <button style={btnPrimary} onClick={openCreate}>+ Nouveau type</button>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Libellé</th>
                <th style={thStyle}>Statut</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Mouvements</th>
                <th style={thStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun type à afficher.</td></tr>
              )}
              {items.map(t => (
                <tr key={t.id} onClick={() => openEdit(t.id)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                  <td style={tdStyle}>{t.label}</td>
                  <td style={tdStyle}>
                    <span style={{ ...(t.isActive ? INVOICED_BADGE_STYLE.yes : { background: '#f3f4f6', color: '#6b7280' }), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                      {t.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#6b7280', fontSize: '0.8125rem' }}>{t._count?.movements ?? 0}</td>
                  <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#9ca3af', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TypeFormModal typeId={editingId} onClose={closeModal} onSaved={onSaved} />
      )}
    </>
  );
}
